import { UploadCloud, Plus, Sparkles, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";

const FILE_FORMATS = ["PDF", "DOCX", "TXT", "PPTX"];

interface UploadAreaProps {
	onClick: (file?: File) => void;
	variant?: "desktop" | "mobile";
}

export function UploadArea({ onClick, variant = "desktop" }: UploadAreaProps) {
	const { t } = useTranslation();

	const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
		onDrop: (acceptedFiles) => {
			if (acceptedFiles.length > 0) {
				onClick(acceptedFiles[0]);
			}
		},
		accept: {
			"application/pdf": [".pdf"],
			"application/vnd.openxmlformats-officedocument.wordprocessingml.document":
				[".docx"],
			"text/plain": [".txt"],
			"application/vnd.openxmlformats-officedocument.presentationml.presentation":
				[".pptx"],
		},
		noClick: true,
		noKeyboard: false,
		multiple: false,
	});

	const handleClick = () => {
		open();
	};

	if (variant === "desktop") {
		return (
			<div {...getRootProps()}>
				<input {...getInputProps()} />
				{/* Animated gradient border wrapper */}
				<div className="relative group rounded-2xl p-[1.5px] transition-all duration-500">
					{/* Animated gradient border -- always subtly visible */}
					<div
						className={`absolute inset-0 rounded-2xl bg-[conic-gradient(from_var(--border-angle),transparent_30%,var(--primary)_50%,transparent_70%)] transition-opacity duration-500 animate-[spin-border_4s_linear_infinite] ${
							isDragActive ? "opacity-80" : "opacity-25 group-hover:opacity-60"
						}`}
						style={
							{
								"--border-angle": "0deg",
							} as React.CSSProperties
						}
					/>

					{/* Card content */}
					<div
						className={`relative liquid-glass flex w-full cursor-pointer items-center justify-center rounded-2xl transition-all duration-300 overflow-hidden py-12 ${
							isDragActive ? "scale-[1.01]" : "hover:scale-[1.005]"
						}`}
						onClick={handleClick}
					>
						{/* Persistent soft radial glow behind icon */}
						<div
							className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full blur-3xl transition-all duration-500 pointer-events-none ${
								isDragActive
									? "bg-primary/12 opacity-100 scale-125"
									: "bg-primary/6 opacity-100 group-hover:bg-primary/10 group-hover:scale-110"
							}`}
						/>

						<div className="relative flex flex-col items-center gap-5 pointer-events-none">
							{/* Icon with ring -- primary by default */}
							<div className="relative">
								<div
									className={`absolute inset-0 rounded-full bg-primary/20 blur-xl transition-all duration-500 ${
										isDragActive
											? "opacity-100 scale-150"
											: "opacity-40 group-hover:opacity-70"
									}`}
								/>
								<div
									className={`relative p-5 rounded-full border transition-all duration-300 ${
										isDragActive
											? "bg-primary/20 border-primary/50 scale-110"
											: "bg-primary/10 border-primary/25 group-hover:bg-primary/15 group-hover:border-primary/40"
									}`}
								>
									{isDragActive ? (
										<Sparkles className="h-10 w-10 text-primary animate-pulse" />
									) : (
										<UploadCloud className="h-10 w-10 text-primary transition-colors duration-300" />
									)}
								</div>
							</div>

							{/* Text content */}
							<div className="text-center space-y-2">
								<h3 className="text-2xl font-bold text-foreground">
									{isDragActive
										? t("dashboard.dropFiles")
										: t("dashboard.createPodWorkspace")}
								</h3>
								<p className="text-sm text-muted-foreground max-w-sm">
									{isDragActive
										? t("dashboard.releaseToUpload")
										: t("dashboard.uploadDropOrClick")}
								</p>
							</div>

							{/* File format badges */}
							{!isDragActive && (
								<div className="flex items-center gap-2 mt-1">
									{FILE_FORMATS.map((format) => (
										<span
											key={format}
											className="px-2.5 py-1 text-[11px] font-medium rounded-md bg-primary/5 text-muted-foreground border border-primary/15 group-hover:border-primary/30 group-hover:text-foreground/80 transition-all duration-300"
										>
											{format}
										</span>
									))}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Mobile variant — primary CTA card
	return (
		<div {...getRootProps()}>
			<input {...getInputProps()} />
			<div
				className={`group relative flex w-full cursor-pointer items-center rounded-xl p-3 border transition-all duration-300 ${
					isDragActive
						? "scale-[1.02] border-primary bg-primary/10"
						: "border-primary/30 bg-primary/5 active:scale-[0.98] active:bg-primary/10"
				}`}
				onClick={handleClick}
			>
				<div className="flex items-center gap-3 pointer-events-none w-full">
					{/* Icon */}
					<div
						className={`p-2.5 rounded-xl shrink-0 transition-all duration-300 ${
							isDragActive ? "bg-primary/20" : "bg-primary/10"
						}`}
					>
						{isDragActive ? (
							<Sparkles className="h-5 w-5 text-primary animate-pulse" />
						) : (
							<Plus className="h-5 w-5 text-primary" />
						)}
					</div>

					{/* Text */}
					<div className="min-w-0 flex-1">
						<span className="text-sm font-semibold text-foreground block">
							{isDragActive
								? t("dashboard.dropFiles")
								: t("dashboard.createPodWorkspace")}
						</span>
						<span className="text-[11px] text-muted-foreground block leading-none mt-0.5">
							{isDragActive
								? t("dashboard.releaseToUpload")
								: FILE_FORMATS.join(", ")}
						</span>
					</div>

					{/* Arrow indicator */}
					<ArrowRight className="h-4 w-4 text-primary/60 shrink-0" />
				</div>
			</div>
		</div>
	);
}
